import { useMemo } from 'react';

import type { Fetcher } from 'api/fetcher';
import { useApi } from 'api/useApi';
import { ENV_VARS } from 'app/config/environment-vars';

import type {
  AutoHealResult,
  CreateSubscriptionModel,
  FeatureRepairResult,
  GetPermissionsResult,
  ServiceStatus,
  SubscriptionUserPermissions,
  SubscriptionUserViewModel,
  SubscriptionViewModel
} from './types';

export const SUBSCRIPTION_CONFIG_KEY = 'ApiResource:SubscriptionApi:Url';
const SUBSCRIPTION_ERROR_KEY = 'SubscriptionsApiClient';

export const useSubscriptionsApi = () => {
  const fetchFunction = useApi(ENV_VARS.SUBSCRIPTIONS_URL, SUBSCRIPTION_ERROR_KEY);

  return useMemo(() => subscriptionsApi(fetchFunction), [fetchFunction]);
};

const subscriptionsApi = (fetchFunction: Fetcher) => {
  const getPermissions = async () => {
    return await fetchFunction<GetPermissionsResult>('/api/permissions');
  };

  const getById = async (subscriptionId: number) => {
    const response = await fetchFunction<SubscriptionViewModel>(`/api/subscriptions/${subscriptionId}`);

    return response;
  };

  const getCurrent = async () => {
    const response = await fetchFunction<SubscriptionViewModel>('/api/subscriptions/current');

    return response;
  };

  const create = async (model: Omit<CreateSubscriptionModel, 'features'>) => {
    const response = await fetchFunction<SubscriptionViewModel>('/api/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ ...model, features: DEFAULT_FEATURES })
    });

    return response;
  };

  const updateName = async (subscriptionId: number, newName: string) => {
    const response = await fetchFunction<SubscriptionViewModel>(`/api/subscriptions/${subscriptionId}/name`, {
      method: 'POST',
      body: JSON.stringify({ newSubscriptionName: newName })
    });

    return response;
  };

  const addUser = async (subscriptionId: number, userEmail: string) => {
    const response = await fetchFunction<SubscriptionUserViewModel>(`/api/subscriptions/${subscriptionId}/users`, {
      method: 'POST',
      body: JSON.stringify({ userEmailAddress: userEmail })
    });

    return response;
  };

  const removeUser = async (subscriptionId: number, userEmail: string) => {
    const response = await fetchFunction(`/api/subscriptions/${subscriptionId}/users:delete`, {
      method: 'POST',
      body: JSON.stringify({ userEmailAddress: userEmail })
    });

    return response;
  };

  const assignFeature = async (
    subscriptionId: number,
    featureId: number,
    subscriptionFeatureId: number,
    subscriptionUserId: number
  ) => {
    // NOTE: the returned number is not the subscriptionId.
    const response = await fetchFunction<number>(`/api/subscriptions/${subscriptionId}/features`, {
      method: 'POST',
      body: JSON.stringify({ featureId, subscriptionFeatureId, subscriptionUserId })
    });

    return response;
  };

  const disableFeature = async (
    subscriptionId: number,
    featureId: number,
    subscriptionFeatureId: number,
    subscriptionUserId: number
  ) => {
    // NOTE: the returned number is not the subscriptionId.
    const response = await fetchFunction<number>(`/api/subscriptions/${subscriptionId}/features:disable`, {
      method: 'POST',
      body: JSON.stringify({ featureId, subscriptionFeatureId, subscriptionUserId })
    });

    return response;
  };

  const deleteSubscription = async (subscriptionId: number) => {
    const response = await fetchFunction<number>(`/api/subscriptions/${subscriptionId}`, {
      method: 'DELETE'
    });

    return response;
  };

  const getStatus = async () => {
    return await fetchFunction<ServiceStatus>('/api/admin/status');
  };

  const getSubscriptionUsers = async (technicalName: string) => {
    return await fetchFunction<string[]>(`/api/user/me?technicalName=${technicalName}`);
  };

  const getSubscriptionUsersPermissions = async (subscriptionId: number) => {
    return await fetchFunction<SubscriptionUserPermissions[]>(`/api/subscriptions/${subscriptionId}/users/permissions`);
  };

  const getSubscriptionUserPermissions = async (subscriptionId: number, userId: number) => {
    return await fetchFunction<SubscriptionUserPermissions>(
      `/api/subscriptions/${subscriptionId}/users/${userId}/permissions`
    );
  };

  const autoHealDatabase = async (databaseName: string) => {
    return await fetchFunction<AutoHealResult>('/api/admin/autoheal-database', {
      method: 'POST',
      body: JSON.stringify({ databaseName })
    });
  };

  const repairFeature = async (featureName: string) => {
    return await fetchFunction<FeatureRepairResult>(`/api/admin/features/${featureName}:repair`, {
      method: 'POST',
      body: JSON.stringify({}) // TODO: check if this empty body is necessary
    });
  };

  return {
    /** Get permissions of the current user. */
    getPermissions,
    /** Get the subscription by Id. */
    getById,
    /** Fetch the subscription that the current user is using */
    getCurrent,
    /** Create a subscription */
    create,
    /** Update the name of the subscription */
    updateName,
    /** Add a user to a subscription */
    addUser,
    /** Remove a user from a subscription */
    removeUser,
    /** Assign a feature to a subscription */
    assignFeature,
    /** Disable a feature of a subscription */
    disableFeature,
    /** Delete a subscription */
    deleteSubscription,
    /** Get the status: all subscriptions, all features, and all databases.  */
    getStatus,
    /** Get all users of the subscription. */
    getSubscriptionUsers,
    /** Get the permissions of the users of the subscription. */
    getSubscriptionUsersPermissions,
    /** Get the permissions of a user of the subscription. */
    getSubscriptionUserPermissions,
    /** Trigger to fix issues in a database */
    autoHealDatabase,
    /** Repair a feature. */
    repairFeature
  };
};

// TODO: move this to BE.
const DEFAULT_FEATURES = {
  ProjectAdministrator: 0,
  ProjectContributor: 0,
  ProjectDataImporter: 0,
  ProjectTeamsAdministrator: 0,
  SubscriptionAdministrator: 0,
  TemplateAdministrator: 0,
  IntegrationsAdministrator: 0
};
